import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  TestTube,
  User,
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  XCircle,
  Edit,
  FileText,
  Trash2,
  Eye,
} from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConciergeTestConfig } from "@/hooks/useConciergeTests";
import { getTestNameFromCode } from "@/utils/testMapping";

const statusConfig = {
  submitted: {
    label: "Submitted",
    icon: RefreshCw,
    color: "bg-blue-100 text-blue-700 border-blue-200",
    iconColor: "text-blue-600",
  },
  scheduled: {
    label: "Scheduled",
    icon: Calendar,
    color: "bg-purple-100 text-purple-700 border-purple-200",
    iconColor: "text-purple-600",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle,
    color: "bg-green-100 text-green-700 border-green-200",
    iconColor: "text-green-600",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    color: "bg-gray-100 text-gray-700 border-gray-200",
    iconColor: "text-gray-600",
  },
  error: {
    label: "Error",
    icon: AlertCircle,
    color: "bg-red-100 text-red-700 border-red-200",
    iconColor: "text-red-600",
  },
  follow_up: {
    label: "Follow-up",
    icon: Calendar,
    color: "bg-orange-100 text-orange-700 border-orange-200",
    iconColor: "text-orange-600",
  },
};

export default function RequestList({
  requests,
  isLoading,
  onEditRequest,
  onDeleteRequest,
  onViewRequest,
  onStatusChange,
  canManageRequests = true,
}) {
  const { data: testConfig } = useConciergeTestConfig();
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array(3)
          .fill(0)
          .map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardContent className="p-12 text-center">
          <TestTube className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            No Requests Found
          </h3>
          <p className="text-slate-600 mb-4">
            Click &quot;New Request&quot; to start.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-6">
      {requests.map((req) => {
        const config = statusConfig[req.status] || statusConfig.submitted;
        return (
          <Card
            key={req.id}
            className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <User className="w-5 h-5 text-slate-600" />
                    <h3 className="font-semibold text-slate-900 text-lg">
                      {req.patientName || "Unknown Patient"}
                    </h3>
                    <Badge className={config.color}>{config.label}</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{req.patientAddress || "No address"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {req.preferredDate
                          ? format(new Date(req.preferredDate), "MMM d, yyyy")
                          : "No date specified"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span className="capitalize">
                        {req.preferredTimeSlot || "No time specified"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="md:text-right flex flex-col gap-2">
                  <div>
                    <p className="text-sm text-slate-500">
                      Requested by Dr. {req.referringDoctor || "Unknown"}
                    </p>
                    <p className="text-xs text-slate-400">
                      on{" "}
                      {req.createdAt
                        ? format(new Date(req.createdAt), "MMM d, yyyy")
                        : "Unknown date"}
                    </p>
                  </div>

                  {/* Status Change Dropdown or Status Badge */}
                  <div className="mb-2">
                    {canManageRequests ? (
                      <Select
                        value={req.status}
                        onValueChange={(newStatus) =>
                          onStatusChange(req.id, newStatus)
                        }
                      >
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                          <SelectItem value="error">Error</SelectItem>
                          <SelectItem value="follow_up">Follow-up</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge
                        className={`${statusConfig[req.status]?.color} inline-flex items-center gap-1 text-xs px-2 py-1`}
                      >
                        {React.createElement(statusConfig[req.status]?.icon, {
                          className: `w-3 h-3 ${statusConfig[req.status]?.iconColor}`,
                        })}
                        {statusConfig[req.status]?.label || req.status}
                      </Badge>
                    )}
                  </div>

                  {canManageRequests && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="self-end"
                        >
                          Actions
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewRequest?.(req)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onEditRequest?.(req)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Request
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDeleteRequest?.(req.id)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Request
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              <div className="mt-6 border-t pt-4">
                <h4 className="font-medium text-slate-800 mb-2">
                  Requested Tests
                </h4>
                <div className="flex flex-wrap gap-2">
                  {req?.requested_tests?.length > 0 ? (
                    req.requested_tests.map((testCode, index) => {
                      const testName = getTestNameFromCode(testCode, testConfig);
                      return (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="font-normal"
                          title={testCode} // Show code on hover
                        >
                          {testName}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="text-slate-500">No tests specified</span>
                  )}
                </div>
              </div>

              {req.clinicalNotes && (
                <div className="mt-4 p-3 bg-amber-50/60 rounded-lg border border-amber-200/60">
                  <h4 className="font-medium text-amber-800 mb-1 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Clinical Notes
                  </h4>
                  <p className="text-sm text-amber-700">{req.clinicalNotes}</p>
                </div>
              )}

              {req.scheduledVisitDatetime && (
                <div className="mt-4 p-3 bg-purple-50/60 rounded-lg border border-purple-200/60">
                  <p className="text-sm font-semibold text-purple-800">
                    Visit Scheduled:{" "}
                    {format(
                      new Date(req.scheduledVisitDatetime),
                      "EEEE, MMM d, yyyy 'at' h:mm a"
                    )}{" "}
                    with {req.assignedNurse || "assigned nurse"}.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
